import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";

import {
  Box,
  ContextMenuButton,
  ContextMenuLink,
  Divider,
  Icon,
  Inline,
  Link,
  Stack,
  Text,
  TextHoverMenu,
  useBreakpoint
} from "@seneca/senekit";
import { TUTORING_LEAD_SOURCES } from "@seneca/tutoring-service-consts";

import { linkToRegionModal } from "common/links";
import { regionCodes } from "common/static/regions";

import { fetchSubscriptionsIfNotFetched } from "seneca-common/features/subscriptions/state/service";
import {
  getRegion,
  getUserAccountType,
  getUserEmail,
  getUserGivenName,
  getUserNickName
} from "seneca-common/features/user/state";
import useDispatchOnMount from "seneca-common/utils/hooks/dispatch/useDispatchOnMount";
import { openWindowSecurely } from "seneca-common/utils/routing";
import { analyticsLogTryAsStudent } from "seneca-common/utils/senecaAnalytics";

import useUpdateApp from "features/app/hooks/useUpdateApp";
import { RocketEmoji } from "features/common/emoji/components/Emoji";
import ParentLogo from "features/common/logos/ParentLogo";
import { NavbarProps, NavbarSideDrawer } from "features/navbar";
import useParentContext from "features/parents/context/useParentContext";
import {
  linkToParentFaqs,
  linkToPersonalInformation,
  makeLinkToParentWhatIsPremium,
  SUBSCRIPTION_JOURNEY_SOURCES
} from "features/parents/links";
import { linkToChildAssignmentsPage } from "features/parents/routes/parent/routes/child/routes/assignments/links";
import { linkToOverviewPage } from "features/parents/routes/parent/routes/child/routes/overview/links";
import { linkToWeeklyReportsForPreviousWeek } from "features/parents/routes/parent/routes/child/routes/weekly-reports/links";
import { useFeedbackLink } from "features/parents/routes/parent/routes/feedback/hooks/useFeedbackLink";
import { linkToTutoringPage } from "features/parents/routes/parent/routes/tutoring-page/links";
import { useIsPlurallApp } from "features/plurall";
import { getLinkToPlurallStore } from "features/plurall/links";
import { isNativeAppiOS } from "features/pwa/mobile/ios/utils/isNativeAppiOS";
import {
  getSellPremiumToUser,
  getSellUpgradeToUser
} from "features/subscriptions/utils/showPremiumAds";
import { setPlurallHelpCentreModalOpen } from "features/ui-state/actions";

import { useGetSelectedChildUserId } from "../../monitoring-overview/hooks/useGetSelectedChild";
import ParentMobileNavbar from "./ParentMobileNavbar";

export default function ParentNavbar({ children, ...props }: NavbarProps) {
  useDispatchOnMount(fetchSubscriptionsIfNotFetched);
  const { isMobileOnly, isTabletOnly } = useBreakpoint();
  const isPlurallApp = useIsPlurallApp();

  const showTutoring =
    useSelector(getRegion) === regionCodes.GB &&
    !isPlurallApp &&
    !isNativeAppiOS();
  const sellUpgradeToUser = useSelector(getSellUpgradeToUser);
  const sellPremiumToUser = useSelector(getSellPremiumToUser);
  const email = useSelector(getUserEmail);
  const selectedChildId = useGetSelectedChildUserId();

  const { setBookTutorForSubject } = useParentContext();
  const resetBookTutorForSubject = () => setBookTutorForSubject(undefined);

  const { update, ready, updating } = useUpdateApp();

  const dispatch = useDispatch();
  const onTryAsAStudentClick = useCallback(
    () => dispatch(analyticsLogTryAsStudent),
    [dispatch]
  );

  const commonProps = {
    ...props,
    email,
    onTryAsAStudentClick,
    sellUpgradeToUser,
    sellPremiumToUser,
    updateAppVersion: update,
    appVersionUpdateReady: ready,
    appVersionIsUpdating: updating,
    showTutoring,
    selectedChildId,
    resetBookTutorForSubject
  };
  if (isMobileOnly)
    return <ParentMobileNavbar {...commonProps}>{children}</ParentMobileNavbar>;

  return (
    <>
      <Box marginX={["large", "xlarge"]} marginY="xlarge" dataTest="navbar">
        <Inline
          flexWrap="nowrap"
          justifyContent="space-between"
          alignItems="center"
        >
          <RouterLink
            to={linkToOverviewPage(selectedChildId)}
            style={{ textDecoration: "none" }}
          >
            <ParentLogo />
          </RouterLink>

          {isTabletOnly ? (
            <ParentTabletNavbar {...commonProps} />
          ) : (
            <ParentDesktopNavbar {...commonProps} />
          )}
        </Inline>
      </Box>
      {children}
    </>
  );
}

function ParentDesktopNavbar({
  onTryAsAStudentClick,
  sellPremiumToUser,
  showTutoring,
  selectedChildId,
  resetBookTutorForSubject,
  ...hoverMenuProps
}: any) {
  const isPlurallApp = useIsPlurallApp();

  const linkToWhatIsPremiumPage = isPlurallApp
    ? getLinkToPlurallStore()
    : linkToWhatIsPremium();

  const linkBookOneToOneTutoring = linkToTutoringPage(
    TUTORING_LEAD_SOURCES.NAVBAR
  );

  return (
    <Inline space="xxlarge">
      <Link navLink to={linkToOverviewPage(selectedChildId)} tone="neutral">
        Home
      </Link>
      {!!selectedChildId && (
        <Link
          navLink
          to={linkToChildAssignmentsPage(selectedChildId)}
          tone="neutral"
        >
          Assignments
        </Link>
      )}
      {!!selectedChildId && (
        <Link
          navLink
          tone="neutral"
          to={linkToWeeklyReportsForPreviousWeek(selectedChildId)}
        >
          Reports
        </Link>
      )}
      {sellPremiumToUser && (
        <Link navLink to={linkToWhatIsPremiumPage} tone="neutral">
          <GetPremium />
        </Link>
      )}
      {showTutoring && (
        <Link
          navLink
          tone="neutral"
          to={linkBookOneToOneTutoring}
          onClick={resetBookTutorForSubject}
        >
          Book a 1:1 tutor
        </Link>
      )}
      {/* @ts-ignore */}
      <ParentDesktopHoverMenu {...hoverMenuProps} />
    </Inline>
  );
}

function ParentDesktopHoverMenu({
  email,
  locale,
  sellUpgradeToUser,
  showRegionLink,
  signOut,
  updateAppVersion,
  appVersionUpdateReady,
  appVersionIsUpdating
}: any) {
  const dispatch = useDispatch();
  const isPlurallApp = useIsPlurallApp();
  const givenName = useSelector(getUserGivenName);
  const nickname = useSelector(getUserNickName);
  const role = useSelector(getUserAccountType);
  const name = role === "schoolteacher" && nickname ? nickname : givenName;

  const parentFeedbackLocation = useFeedbackLink();

  return (
    <TextHoverMenu position="bottomLeft">
      {{
        label: (
          <Text dataNoTranslate size="medium" weight="bold">
            {name}
          </Text>
        ),
        options: (
          <Box width="200px" margin="large">
            <Stack space="large">
              {!isPlurallApp && (
                <>
                  <Box margin="large">
                    <Text dataNoTranslate size="small" tone="neutral" truncate>
                      {email}
                    </Text>
                  </Box>

                  <Divider direction="horizontal" />
                </>
              )}

              {sellUpgradeToUser && (
                <Stack space="large">
                  <ContextMenuLink to={linkToWhatIsPremium()}>
                    <UpgradePremium />
                  </ContextMenuLink>
                  <Divider direction="horizontal" />
                </Stack>
              )}

              <Stack>
                {!isPlurallApp && (
                  <ContextMenuLink to={parentFeedbackLocation}>
                    {{
                      icon: (
                        <Icon
                          iconName="megaphone"
                          size="medium"
                          accessibilityLabel={null}
                          tone="neutral"
                        />
                      ),
                      text: <Text>Feedback</Text>
                    }}
                  </ContextMenuLink>
                )}
                <ContextMenuLink to={linkToPersonalInformation()}>
                  {{
                    icon: (
                      <Icon
                        iconName="cog"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Settings</Text>
                  }}
                </ContextMenuLink>

                <ContextMenuButton
                  onClick={() =>
                    isPlurallApp
                      ? dispatch(setPlurallHelpCentreModalOpen())
                      : openWindowSecurely(linkToParentFaqs(locale))
                  }
                >
                  {{
                    icon: (
                      <Icon
                        iconName="help"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Help centre</Text>
                  }}
                </ContextMenuButton>

                {showRegionLink && (
                  <ContextMenuLink to={linkToRegionModal()}>
                    {{
                      icon: (
                        <Icon
                          iconName="globe"
                          size="medium"
                          accessibilityLabel={null}
                          tone="neutral"
                        />
                      ),
                      text: <Text>Region</Text>
                    }}
                  </ContextMenuLink>
                )}
              </Stack>

              <Divider direction="horizontal" />

              {appVersionUpdateReady && [
                <Stack>
                  <ContextMenuButton onClick={updateAppVersion}>
                    {{
                      icon: (
                        <Icon
                          iconName="reassign"
                          size="medium"
                          accessibilityLabel={null}
                          tone="neutral"
                        />
                      ),
                      text: (
                        <Text>
                          {appVersionIsUpdating ? (
                            <>Updating...</>
                          ) : (
                            <>Update Seneca</>
                          )}
                        </Text>
                      )
                    }}
                  </ContextMenuButton>
                </Stack>,
                <Divider direction="horizontal" />
              ]}

              <ContextMenuButton onClick={signOut}>
                {{
                  icon: (
                    <Icon
                      iconName="leave"
                      size="medium"
                      accessibilityLabel={null}
                      tone="neutral"
                    />
                  ),
                  text: <Text>Logout</Text>
                }}
              </ContextMenuButton>
            </Stack>
          </Box>
        )
      }}
    </TextHoverMenu>
  );
}

function ParentTabletNavbar({
  email,
  signOut,
  showRegionLink,
  showTutoring,
  locale,
  sellUpgradeToUser,
  sellPremiumToUser,
  updateAppVersion,
  appVersionUpdateReady,
  appVersionIsUpdating,
  selectedChildId,
  resetBookTutorForSubject
}: any) {
  const dispatch = useDispatch();
  const isPlurallApp = useIsPlurallApp();

  const linkBookOneToOneTutoring = linkToTutoringPage(
    TUTORING_LEAD_SOURCES.NAVBAR
  );

  const parentFeedbackLocation = useFeedbackLink();

  return (
    <NavbarSideDrawer>
      {{
        options: (closeDrawer: any) => (
          <Stack space="large">
            {!isPlurallApp && (
              <>
                <Box margin="large">
                  <Text dataNoTranslate size="small" tone="neutral" truncate>
                    {email}
                  </Text>
                </Box>

                <Divider direction="horizontal" />
              </>
            )}

            <Stack>
              <ContextMenuLink
                onClick={closeDrawer}
                to={linkToOverviewPage(selectedChildId)}
              >
                <Text>Home</Text>
              </ContextMenuLink>
              {!!selectedChildId && (
                <ContextMenuLink
                  onClick={closeDrawer}
                  to={linkToChildAssignmentsPage(selectedChildId)}
                >
                  <Text>Assignments</Text>
                </ContextMenuLink>
              )}
              {!!selectedChildId && (
                <ContextMenuLink
                  onClick={closeDrawer}
                  to={linkToWeeklyReportsForPreviousWeek(selectedChildId)}
                >
                  <Text>Reports</Text>
                </ContextMenuLink>
              )}
              {sellPremiumToUser && !isPlurallApp && (
                <ContextMenuLink
                  onClick={closeDrawer}
                  to={linkToWhatIsPremium()}
                >
                  <Text>
                    <GetPremium />
                  </Text>
                </ContextMenuLink>
              )}
              {sellUpgradeToUser && (
                <ContextMenuLink
                  onClick={closeDrawer}
                  to={linkToWhatIsPremium()}
                >
                  <Text>
                    <UpgradePremium />
                  </Text>
                </ContextMenuLink>
              )}
              {showTutoring && (
                <ContextMenuLink
                  to={linkBookOneToOneTutoring}
                  onClick={() => {
                    closeDrawer();
                    resetBookTutorForSubject();
                  }}
                >
                  <Text>Book a 1:1 tutor</Text>
                </ContextMenuLink>
              )}
            </Stack>

            <Divider direction="horizontal" />

            <Stack>
              <ContextMenuLink
                to={parentFeedbackLocation}
                onClick={closeDrawer}
              >
                {{
                  icon: (
                    <Icon
                      iconName="megaphone"
                      size="medium"
                      accessibilityLabel={null}
                      tone="neutral"
                    />
                  ),
                  text: <Text>Feedback</Text>
                }}
              </ContextMenuLink>

              <ContextMenuLink
                onClick={closeDrawer}
                to={linkToPersonalInformation()}
              >
                {{
                  icon: (
                    <Icon
                      iconName="cog"
                      size="medium"
                      accessibilityLabel={null}
                      tone="neutral"
                    />
                  ),
                  text: <Text>Settings</Text>
                }}
              </ContextMenuLink>

              <ContextMenuButton
                onClick={() =>
                  isPlurallApp
                    ? dispatch(setPlurallHelpCentreModalOpen())
                    : openWindowSecurely(linkToParentFaqs(locale))
                }
              >
                {{
                  icon: (
                    <Icon
                      iconName="help"
                      size="medium"
                      accessibilityLabel={null}
                      tone="neutral"
                    />
                  ),
                  text: <Text>Help centre</Text>
                }}
              </ContextMenuButton>

              {showRegionLink && (
                <ContextMenuLink onClick={closeDrawer} to={linkToRegionModal()}>
                  {{
                    icon: (
                      <Icon
                        iconName="globe"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Region</Text>
                  }}
                </ContextMenuLink>
              )}
            </Stack>

            <Divider direction="horizontal" />

            {appVersionUpdateReady && [
              <Stack>
                <ContextMenuButton onClick={updateAppVersion}>
                  {{
                    icon: (
                      <Icon
                        iconName="reassign"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: (
                      <Text>
                        {appVersionIsUpdating ? (
                          <>Updating...</>
                        ) : (
                          <>Update Seneca</>
                        )}
                      </Text>
                    )
                  }}
                </ContextMenuButton>
              </Stack>,
              <Divider direction="horizontal" />
            ]}

            <ContextMenuButton onClick={signOut}>
              {{
                icon: (
                  <Icon
                    iconName="leave"
                    size="medium"
                    accessibilityLabel={null}
                    tone="neutral"
                  />
                ),
                text: <Text>Logout</Text>
              }}
            </ContextMenuButton>
          </Stack>
        )
      }}
    </NavbarSideDrawer>
  );
}

const GetPremium = () => (
  <Inline space="medium">
    <RocketEmoji />
    Get premium
  </Inline>
);

const UpgradePremium = () => (
  <Inline space="medium">
    <RocketEmoji />
    Upgrade
  </Inline>
);

function linkToWhatIsPremium() {
  return makeLinkToParentWhatIsPremium({
    source: SUBSCRIPTION_JOURNEY_SOURCES.PARENT_NAVBAR
  });
}
